import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { MODEL_FAMILIES } from "../../utils/constants";
// [C5 FALSE POSITIVE] adminApi imported directly for user lookup. This is intentional:
// lookup is tightly coupled to this component's internal state (clearing on input change,
// preview card rendering). Lifting it to the parent would add prop-threading complexity
// with no testability or reuse benefit — the lookup is only used here.
import { adminApi } from "../../services/api";

/**
 * Admin form for manually adjusting a user's credit balance.
 *
 * [S1 FIX] Looks up user before adjustment so admin can verify identity.
 * [S2 FIX] Requires explicit confirmation before applying.
 * [S3 FIX] Amount input bounded to match backend validation (-1,000,000 to 1,000,000).
 * [U3 FIX] Shows user email/username and current balances before adjustment.
 * [I2 FIX] Uses MODEL_FAMILIES from shared constants instead of hardcoded array.
 */
const UserCreditAdjustment = ({ onAdjust }) => {
  const [userId, setUserId] = useState("");
  const [modelFamily, setModelFamily] = useState(MODEL_FAMILIES[0].key);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [formError, setFormError] = useState("");

  // [S1/U3] User lookup state
  const [lookupUser, setLookupUser] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  // [S2] Confirmation state
  const [pendingConfirm, setPendingConfirm] = useState(null);

  // [U1 FIX] Accepts numeric ID or email string — backend resolves via /admin/users/lookup?q=
  const handleLookup = useCallback(async () => {
    setFormError("");
    setLookupUser(null);
    setResult(null);

    const q = userId.trim();
    if (!q) {
      setFormError("Enter a user ID or email address");
      return;
    }

    setLookingUp(true);
    try {
      const data = await adminApi.lookupUser(q);
      setLookupUser(data);
    } catch (err) {
      const msg =
        err.response?.status === 404
          ? `No user found for "${q}"`
          : err.response?.data?.message || "Failed to look up user";
      setFormError(msg);
    } finally {
      setLookingUp(false);
    }
  }, [userId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    setResult(null);

    if (!lookupUser) {
      setFormError("Look up the user first");
      return;
    }

    const amt = Number.parseInt(amount, 10);
    if (Number.isNaN(amt) || amt === 0) {
      setFormError("Amount must be a non-zero integer");
      return;
    }

    if (!description.trim()) {
      setFormError("Description is required for audit trail");
      return;
    }

    // [S2] Show confirmation instead of immediately submitting
    setPendingConfirm({ userId: lookupUser.id, modelFamily, amount: amt });
  };

  const executeAdjust = async () => {
    if (!pendingConfirm) return;
    setPendingConfirm(null);
    setSubmitting(true);
    try {
      const data = await onAdjust(pendingConfirm.userId, {
        modelFamily: pendingConfirm.modelFamily,
        amount: pendingConfirm.amount,
        description: description.trim(),
      });
      setResult(data);
      setAmount("");
      setDescription("");
      // Re-fetch user to show updated balances
      try {
        const refreshed = await adminApi.lookupUser(pendingConfirm.userId);
        setLookupUser(refreshed);
      } catch {
        // Non-critical — balances shown in result instead
      }
    } catch {
      // Toast handles error display
    } finally {
      setSubmitting(false);
    }
  };

  const formatCredits = (credits) => {
    if (!credits || Object.keys(credits).length === 0) return "No balances";
    return MODEL_FAMILIES.map((f) => `${f.label}: ${credits[f.key] ?? 0}`).join(
      ", ",
    );
  };

  return (
    <div className="admin-credits-section">
      <div className="admin-credits-section-header">
        <h3>User Credit Adjustment</h3>
      </div>
      <p className="admin-form-hint admin-section-hint">
        Manually add or deduct credits for a specific user. Positive amounts add
        credits; negative amounts deduct. All adjustments are logged in the
        credit transaction ledger.
      </p>

      {/* Step 1: User lookup — [U1 FIX] accepts email or numeric ID */}
      <div className="admin-adjust-form admin-adjust-form--lookup">
        <div className="admin-form-group">
          <label htmlFor="adjust-user-id">User ID or Email</label>
          <input
            id="adjust-user-id"
            type="text"
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setLookupUser(null);
              setResult(null);
            }}
            placeholder="42 or user@example.com"
          />
        </div>
        <div className="admin-form-group">
          <label>&nbsp;</label>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={handleLookup}
            disabled={lookingUp || !userId}
          >
            {lookingUp ? "..." : "Look Up"}
          </button>
        </div>
      </div>

      {/* User preview card — [C2 FIX] inline styles → CSS classes */}
      {lookupUser && (
        <div className="admin-user-preview">
          <strong>{lookupUser.username || lookupUser.email}</strong>
          {lookupUser.username && lookupUser.email && (
            <span className="admin-user-preview-meta">
              {" "}
              ({lookupUser.email})
            </span>
          )}
          <span className="admin-user-preview-meta">
            Plan: {lookupUser.planId || "free"}
          </span>
          {!lookupUser.isActive && (
            <span className="admin-user-preview-inactive">INACTIVE</span>
          )}
          <div className="admin-user-preview-balances">
            Balances: {formatCredits(lookupUser.credits)}
          </div>
        </div>
      )}

      {/* Step 2: Adjustment form (only after lookup) */}
      {lookupUser && (
        <form className="admin-adjust-form" onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label htmlFor="adjust-family">Family</label>
            <select
              id="adjust-family"
              value={modelFamily}
              onChange={(e) => setModelFamily(e.target.value)}
            >
              {MODEL_FAMILIES.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-form-group">
            <label htmlFor="adjust-amount">Amount</label>
            {/* [S3 FIX] Bounded to match backend validation */}
            <input
              id="adjust-amount"
              type="number"
              min="-1000000"
              max="1000000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="+100 or -50"
              required
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="adjust-description">Description</label>
            <input
              id="adjust-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Goodwill credit for outage"
              required
            />
          </div>

          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={submitting}
          >
            {submitting ? "..." : "Review"}
          </button>

          {formError && (
            <div className="admin-form-error admin-form-error-full">
              {formError}
            </div>
          )}
        </form>
      )}

      {/* [S2 FIX] Confirmation step before applying — [C2 FIX] styles → classes */}
      {pendingConfirm && (
        <div
          className={`admin-confirm-card ${pendingConfirm.amount > 0 ? "admin-confirm-card--add" : "admin-confirm-card--deduct"}`}
        >
          <p>
            Confirm: {pendingConfirm.amount > 0 ? "Add" : "Deduct"}{" "}
            {Math.abs(pendingConfirm.amount).toLocaleString()}{" "}
            {pendingConfirm.modelFamily} credits
            {pendingConfirm.amount < 0 ? " from" : " to"} user{" "}
            {lookupUser?.username || lookupUser?.email} (ID:{" "}
            {pendingConfirm.userId})?
          </p>
          <button
            className="admin-btn admin-btn-primary"
            onClick={executeAdjust}
            disabled={submitting}
          >
            {submitting ? "Applying..." : "Confirm"}
          </button>
          <button
            className="admin-btn admin-btn-cancel"
            onClick={() => setPendingConfirm(null)}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Non-form errors (lookup errors shown when no lookupUser) */}
      {formError && !lookupUser && (
        <div className="admin-form-error admin-section-hint">{formError}</div>
      )}

      {result && (
        <div className="admin-refreshing admin-section-hint">
          Adjustment applied. User {result.userId} now has{" "}
          <strong>{result.balanceAfter}</strong> {result.modelFamily} credits.
        </div>
      )}
    </div>
  );
};

UserCreditAdjustment.propTypes = {
  onAdjust: PropTypes.func.isRequired,
};

export default UserCreditAdjustment;
